import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Smartphone, Save, AlertTriangle } from 'lucide-react';
import {
  APP_VERSION_FLAVORS,
  emptyPolicy,
  listAppVersionPolicies,
  saveAppVersionPolicy,
  fetchLiveAppVersions,
  autoVersionForFlavor,
} from '../../services/appVersionControlService';

const Toggle = ({ on, onChange, label, hint, danger }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    className="flex w-full items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-slate-300"
  >
    <div>
      <p className="text-sm font-semibold text-slate-900">{label}</p>
      {hint ? <p className="mt-0.5 text-xs text-slate-500">{hint}</p> : null}
    </div>
    <span
      className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full transition ${
        on ? (danger ? 'bg-red-600' : 'bg-emerald-600') : 'bg-slate-200'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          on ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </span>
  </button>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    {children}
  </label>
);

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400';

export default function AppVersionControl() {
  const [rows, setRows] = useState(() => APP_VERSION_FLAVORS.map((f) => emptyPolicy(f.id)));
  const [loading, setLoading] = useState(true);
  const [savingFlavor, setSavingFlavor] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  const [liveByFlavor, setLiveByFlavor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [data, live] = await Promise.all([
        listAppVersionPolicies(),
        fetchLiveAppVersions().catch(() => null),
      ]);
      setLiveByFlavor(live);
      const byFlavor = Object.fromEntries((data || []).map((r) => [r.flavor, r]));
      setRows(
        APP_VERSION_FLAVORS.map((f) => {
          const auto = autoVersionForFlavor(f.id, live);
          const r = byFlavor[f.id];
          const base = r
            ? {
                ...emptyPolicy(f.id),
                ...r,
                latest_build: r.latest_build ?? '',
                min_build: r.min_build ?? '',
                play_store_url: r.play_store_url || '',
                app_store_url: r.app_store_url || '',
                update_title: r.update_title || '',
                update_message: r.update_message || '',
              }
            : emptyPolicy(f.id);
          return {
            ...base,
            latest_version: auto.version || base.latest_version,
            latest_build: auto.build || base.latest_build,
            min_version: base.min_version || auto.version,
            min_build: base.min_build === '' || base.min_build == null ? auto.build : base.min_build,
          };
        }),
      );
    } catch (e) {
      setError(
        e?.message?.includes('app_version_policies') || /schema cache|PGRST/i.test(String(e?.message || ''))
          ? 'Pehle supabase/app-version-control.sql chalao (Supabase SQL Editor).'
          : e?.message || 'Could not load version policies',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (flavor, next) => {
    setRows((prev) => prev.map((r) => (r.flavor === flavor ? { ...r, ...next } : r)));
  };

  const save = async (row) => {
    const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    setSavingFlavor(row.flavor);
    setError('');
    setFlash('');
    try {
      await saveAppVersionPolicy(row, currentUser?.email || currentUser?.name || 'admin');
      setFlash(`${row.flavor} saved`);
      await load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSavingFlavor(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">App Version Control</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Latest version khud aati hai — jo apps users khol rahe hain, plus current store release.
            Force / optional prompt on karke Save dabao.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {flash}
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <p className="font-semibold text-slate-800">Kaise use karein</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>
            <b>Latest version</b> auto hai (apps + current release). Manual type ki zaroorat nahi.
          </li>
          <li>
            Latest se purani app par iOS aur Play Store dono pe <b>bottom drawer</b> aayega — popup nahi.
            Update tap pe seedha App Store / Play Store khulega.
          </li>
          <li>
            <b>Force update</b> on = min version se neeche par drawer band nahi hoga, sirf Update.
          </li>
        </ol>
      </div>

      <div className="grid gap-5 xl:grid-cols-3">
        {rows.map((row) => {
          const meta = APP_VERSION_FLAVORS.find((f) => f.id === row.flavor);
          const auto = autoVersionForFlavor(row.flavor, liveByFlavor);
          const live = auto.live || {};
          return (
            <section key={row.flavor} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                  <Smartphone size={20} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">{meta?.label}</h2>
                  <p className="text-xs text-slate-500">{meta?.hint}</p>
                </div>
              </div>

              <div className="mb-4 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <p>
                  <span className="font-semibold text-slate-800">Auto latest:</span>{' '}
                  {auto.version || '—'}
                  {auto.build ? ` (${auto.build})` : ''}
                </p>
                <p className="mt-0.5">
                  Release file: {auto.shipped?.version || '—'}
                  {auto.shipped?.build != null ? ` (${auto.shipped.build})` : ''}
                  {live.users
                    ? ` · Live in apps: ${live.version}${live.build ? ` (${live.build})` : ''} · ${live.users} users`
                    : ' · Abhi koi user report nahi (app open ke baad aayega)'}
                </p>
              </div>

              {row.force_update ? (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  Force on hai — {row.min_version || 'min version'} se purani apps block hongi.
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Latest version (auto)">
                  <input
                    className={`${inputClass} bg-slate-50`}
                    value={row.latest_version}
                    readOnly
                    title="Auto from apps + current release"
                  />
                </Field>
                <Field label="Min version (force below this)">
                  <input
                    className={inputClass}
                    value={row.min_version}
                    onChange={(e) => patch(row.flavor, { min_version: e.target.value })}
                    placeholder="1.4"
                  />
                </Field>
                <Field label="Latest build (auto)">
                  <input
                    className={`${inputClass} bg-slate-50`}
                    value={row.latest_build}
                    readOnly
                  />
                </Field>
                <Field label="Min build (optional)">
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    value={row.min_build}
                    onChange={(e) => patch(row.flavor, { min_build: e.target.value })}
                    placeholder="22"
                  />
                </Field>
              </div>

              <div className="mt-3 space-y-2">
                <Toggle
                  on={row.force_update}
                  danger
                  onChange={(on) =>
                    patch(row.flavor, {
                      force_update: on,
                      min_version: row.min_version || row.latest_version,
                    })
                  }
                  label="Force update"
                  hint="Purani app open karte hi bottom drawer. Update Store pe khulega, back se band nahi."
                />
                <Toggle
                  on={row.prompt_update}
                  onChange={(on) => patch(row.flavor, { prompt_update: on })}
                  label="Optional update prompt"
                  hint="Nayi version pe drawer. Force off ho to bhi latest se peeche par drawer aayega."
                />
              </div>

              <div className="mt-3 space-y-3">
                <Field label="Play Store URL">
                  <input
                    className={inputClass}
                    value={row.play_store_url}
                    onChange={(e) => patch(row.flavor, { play_store_url: e.target.value })}
                    placeholder="https://play.google.com/store/apps/details?id=..."
                  />
                </Field>
                <Field label="App Store URL">
                  <input
                    className={inputClass}
                    value={row.app_store_url}
                    onChange={(e) => patch(row.flavor, { app_store_url: e.target.value })}
                    placeholder="https://apps.apple.com/..."
                  />
                </Field>
                <Field label="Dialog title">
                  <input
                    className={inputClass}
                    value={row.update_title}
                    onChange={(e) => patch(row.flavor, { update_title: e.target.value })}
                  />
                </Field>
                <Field label="Dialog message">
                  <textarea
                    className={`${inputClass} min-h-[72px]`}
                    value={row.update_message}
                    onChange={(e) => patch(row.flavor, { update_message: e.target.value })}
                  />
                </Field>
              </div>

              {row.updated_at ? (
                <p className="mt-3 text-[11px] text-slate-400">
                  Last saved {new Date(row.updated_at).toLocaleString('en-IN')}
                  {row.updated_by ? ` · ${row.updated_by}` : ''}
                </p>
              ) : null}

              <button
                type="button"
                disabled={savingFlavor === row.flavor || loading}
                onClick={() => save(row)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save size={16} />
                {savingFlavor === row.flavor ? 'Saving…' : 'Save'}
              </button>
            </section>
          );
        })}
      </div>
    </div>
  );
}
